import { useState } from 'react';
import { useToast } from '@/components/Toast';
import { MultiClientPicker, type SelectedClient } from '@/components/MultiClientPicker';
import {
  SubClientPanel, TERMINATION_REASON_OPTIONS, CRUNCHAFI_TRANSITION_DOC_URL, type SubClient,
} from '@/components/TerminationExtras';
import { submitErrorMessage } from '@/lib/formErrors';
import type { ClientFormBody } from '@/api/hooks';

import { todayLocalISO as todayISO } from '@/lib/date';

/** Termination reasons that show the optional details box: Client Terminated Services (3), Other (5). */
const DETAIL_REASONS = [3, 5];

interface SubmitLike {
  mutateAsync: (body: ClientFormBody) => Promise<{ submissionId: number }>;
  isPending: boolean;
}

/** Shared client request form (Termination / Reactivation): a multi-select client
 *  picker, an effective date, notes, and Submit. Differs only by client scope,
 *  labels, and which submit mutation is passed in. Termination (item 15) adds the
 *  master/sub-client selection, Termination Reason and Crunchafi questions. */
export function ClientRequestForm({
  title, description, scope, dateLabel, submit, termination,
}: {
  title: string;
  description: string;
  scope: 'active' | 'all';
  dateLabel: string;
  submit: SubmitLike;
  termination?: boolean;
}) {
  const notify = useToast();
  const [clients, setClients] = useState<SelectedClient[]>([]);
  const [effectiveDate, setEffectiveDate] = useState(todayISO());
  const [notes, setNotes] = useState('');
  // Termination only
  const [subSel, setSubSel] = useState<Record<string, string[]>>({});       // master -> checked sub IDs
  const [subLists, setSubLists] = useState<Record<string, SubClient[] | null>>({}); // master -> active subs
  const [newMaster, setNewMaster] = useState<Record<string, string>>({});   // master -> new master ID
  const [reason, setReason] = useState<number | null>(null);
  const [reasonOther, setReasonOther] = useState('');
  const [crunchafi, setCrunchafi] = useState<'' | 'Yes' | 'No'>('');

  const pickedIds = new Set(clients.map((c) => c.clientId));
  // Sub-clients checked under a master that is also selected.
  const checkedSubs = new Map<string, { sub: SubClient; master: string }>();
  if (termination) {
    for (const c of clients) {
      const list = subLists[c.clientId] ?? [];
      for (const id of subSel[c.clientId] ?? []) {
        const sub = list.find((s) => s.ClientID === id);
        if (sub && !checkedSubs.has(id)) checkedSubs.set(id, { sub, master: c.clientId });
      }
    }
  }
  const stayingFor = (master: string) =>
    (subLists[master] ?? []).filter((s) => !checkedSubs.has(s.ClientID) && !pickedIds.has(s.ClientID));
  // Subs of selected masters are chosen only in their master's box, not in the search results.
  const hiddenSubs = new Set(
    clients.flatMap((c) => (subLists[c.clientId] ?? []).map((s) => s.ClientID)),
  );

  // When a master's sub-clients load, any of them already picked in the search move
  // into the master's box, selected.
  function onSubsLoaded(master: string, subs: SubClient[] | null) {
    setSubLists((m) => ({ ...m, [master]: subs }));
    if (!subs?.length) return;
    const ids = new Set(subs.map((s) => s.ClientID));
    const moved = clients.filter((c) => ids.has(c.clientId)).map((c) => c.clientId);
    if (moved.length === 0) return;
    setClients((cs) => cs.filter((c) => !ids.has(c.clientId)));
    setSubSel((m) => ({ ...m, [master]: [...new Set([...(m[master] ?? []), ...moved])] }));
  }

  const termInvalid = termination && (
    reason === null || !crunchafi ||
    clients.some((c) => subLists[c.clientId] === undefined || subLists[c.clientId] === null) || // still loading
    clients.some((c) => {
      const staying = stayingFor(c.clientId);
      return staying.length > 0 && !staying.some((s) => s.ClientID === newMaster[c.clientId]);
    })
  );
  const invalid = clients.length === 0 || !effectiveDate || termInvalid;

  function reset() {
    setClients([]); setEffectiveDate(todayISO()); setNotes('');
    setSubSel({}); setSubLists({}); setNewMaster({});
    setReason(null); setReasonOther(''); setCrunchafi('');
  }

  function buildClients(): ClientFormBody['clients'] {
    if (!termination) return clients.map((c) => ({ clientId: c.clientId, clientName: c.clientName }));
    const rows: ClientFormBody['clients'] = [];
    for (const c of clients) {
      if (checkedSubs.has(c.clientId)) continue; // sent below under its master
      const staying = stayingFor(c.clientId);
      const nm = staying.find((s) => s.ClientID === newMaster[c.clientId]);
      rows.push({
        clientId: c.clientId, clientName: c.clientName,
        ...(nm ? { newMasterClientId: nm.ClientID, newMasterClientName: nm.ClientName } : {}),
      });
    }
    for (const { sub, master } of checkedSubs.values()) {
      rows.push({ clientId: sub.ClientID, clientName: sub.ClientName, parentClientId: master });
    }
    return rows;
  }

  async function onSubmit() {
    try {
      const res = await submit.mutateAsync({
        clients: buildClients(),
        // Item 21: Termination has no date field; the effective date is the day it's submitted.
        effectiveDate: termination ? todayISO() : effectiveDate,
        notes,
        ...(termination ? {
          terminationReasonCode: reason ?? undefined,
          terminationReasonOther: reason !== null && DETAIL_REASONS.includes(reason) ? reasonOther.trim() : '',
          crunchafiDisable: crunchafi || undefined,
        } : {}),
      });
      notify('success', `${title.replace(/ Request$/, '')} request #${res.submissionId} submitted. A receipt has been emailed to you.`);
      reset();
    } catch (e) {
      notify('error', submitErrorMessage(e));
    }
  }

  const busy = submit.isPending;

  return (
    <div className="w-full max-w-4xl space-y-4 p-6">
      <div className="card">
        <h2 className="text-lg font-semibold text-foreground">{title}</h2>
        <p className="mt-1 text-sm text-muted-foreground">{description}</p>
      </div>

      <div className="card space-y-4">
        <div>
          <label className="label">Client(s) *</label>
          <MultiClientPicker scope={scope} value={clients} onChange={setClients} disabled={busy}
            hideIds={termination ? hiddenSubs : undefined}
            hint={termination && hiddenSubs.size > 0 ? 'Sub-clients of selected masters are chosen in the box below.' : undefined} />
        </div>

        {termination && clients.map((c) => (
          <SubClientPanel
            key={c.clientId}
            master={c.clientId}
            masterName={c.clientName}
            selected={subSel[c.clientId] ?? []}
            onSelected={(ids) => setSubSel((m) => ({ ...m, [c.clientId]: ids }))}
            newMaster={newMaster[c.clientId] ?? ''}
            onNewMaster={(id) => setNewMaster((m) => ({ ...m, [c.clientId]: id }))}
            onLoaded={(subs) => onSubsLoaded(c.clientId, subs)}
            disabled={busy}
          />
        ))}

        {!termination && <div className="max-w-xs">
          <label className="label">{dateLabel} *</label>
          <input type="date" className="input" value={effectiveDate} disabled={busy} onChange={(e) => setEffectiveDate(e.target.value)} />
        </div>}

        {termination && (
          <>
            <div>
              <label className="label">Termination Reason *</label>
              <div className="space-y-1">
                {TERMINATION_REASON_OPTIONS.map((o) => (
                  <label key={o.code} className="flex items-center gap-2 text-sm">
                    <input type="radio" name="termReason" disabled={busy} checked={reason === o.code} onChange={() => setReason(o.code)} />
                    {o.label}
                  </label>
                ))}
              </div>
              {reason !== null && DETAIL_REASONS.includes(reason) && (
                <input className="input mt-2 max-w-md" placeholder="Additional details (optional)" maxLength={500}
                  value={reasonOther} disabled={busy} onChange={(e) => setReasonOther(e.target.value)} />
              )}
            </div>

            <div>
              <label className="label">Crunchafi *</label>
              <p className="mb-2 text-sm text-muted-foreground">
                Check if Crunchafi services should be disabled. Additionally, if the client will be transferring their
                Crunchafi account to another firm, the accountant should provide them with Jess Nolan's contact
                information (<a className="text-primary underline" href="mailto:Jess.nolan@crunchafi.com">Jess.nolan@crunchafi.com</a>)
                and share the{' '}
                {CRUNCHAFI_TRANSITION_DOC_URL ? (
                  <a className="text-primary underline" href={CRUNCHAFI_TRANSITION_DOC_URL} target="_blank" rel="noreferrer">
                    Transition of your Crunchafi Lease Accounting
                  </a>
                ) : 'Transition of your Crunchafi Lease Accounting'}{' '}
                document.
              </p>
              <div className="flex gap-6">
                {(['Yes', 'No'] as const).map((v) => (
                  <label key={v} className="flex items-center gap-2 text-sm">
                    <input type="radio" name="crunchafi" disabled={busy} checked={crunchafi === v} onChange={() => setCrunchafi(v)} />
                    {v}
                  </label>
                ))}
              </div>
            </div>
          </>
        )}

        <div>
          <label className="label">Notes</label>
          <textarea className="input" rows={2} value={notes} disabled={busy} onChange={(e) => setNotes(e.target.value)} />
        </div>

        <p className="text-xs font-semibold text-accent">*  Mandatory Fields</p>

        <div>
          <button className="btn-success" disabled={invalid || busy} onClick={onSubmit}>
            {busy ? 'Submitting…' : 'Submit'}
          </button>
        </div>
      </div>
    </div>
  );
}
